import React, { useState } from 'react';
import axios from 'axios';
import './ExportButton.css';
import { API_BASE_URL } from '../lib/api';

function ExportButton() {
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  /**
   * Handle export generation
   * @param {string} format - 'pdf' or 'excel'
   */
  const handleExport = async (format) => {
    setShowMenu(false);
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Get CSRF token
      const csrfResponse = await axios.get(`${API_BASE_URL}/csrf-token`, {
        withCredentials: true
      });
      const csrfToken = csrfResponse.data.csrfToken;

      // Request export with default options (last 30 days, no closed positions)
      const response = await axios.post(
        `${API_BASE_URL}/exports/${format}`,
        {
          dateRange: {
            // Default: last 30 days
            startDate: null,
            endDate: null
          },
          includeClosedPositions: false
        },
        {
          headers: {
            'X-CSRF-Token': csrfToken
          },
          withCredentials: true,
          responseType: 'blob' // Important for file download
        }
      );

      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `portfolio-${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(`${format.toUpperCase()} export downloaded successfully!`);
      setTimeout(() => setSuccess(null), 5000);

    } catch (err) {
      console.error('Export error:', err);

      if (err.response?.status === 429) {
        setError('Too many export requests. Please wait a minute and try again.');
      } else if (err.response?.status === 503) {
        setError('Export generation timed out. Please try again with a smaller date range.');
      } else {
        setError('Failed to generate export. Please try again.');
      }

      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="export-button-container">
      <button
        className="export-button"
        onClick={() => setShowMenu(!showMenu)}
        disabled={loading}
        title="Export Portfolio"
      >
        {loading ? (
          <>
            <span className="export-spinner"></span>
            <span>Exporting...</span>
          </>
        ) : (
          <>
            <span className="export-icon">📥</span>
            <span>Export</span>
          </>
        )}
      </button>

      {showMenu && !loading && (
        <div className="export-menu">
          <button
            className="export-menu-item"
            onClick={() => handleExport('pdf')}
          >
            <span className="export-format-icon">📄</span>
            <div className="export-menu-item-content">
              <div className="export-format-name">PDF Report</div>
              <div className="export-format-desc">Professional printable report</div>
            </div>
          </button>

          <button
            className="export-menu-item"
            onClick={() => handleExport('excel')}
          >
            <span className="export-format-icon">📊</span>
            <div className="export-menu-item-content">
              <div className="export-format-name">Excel Spreadsheet</div>
              <div className="export-format-desc">Editable data with formulas</div>
            </div>
          </button>
        </div>
      )}

      {/* Success/Error Messages */}
      {success && (
        <div className="export-message export-success">
          ✓ {success}
        </div>
      )}

      {error && (
        <div className="export-message export-error">
          ✗ {error}
        </div>
      )}

      {/* Click-away overlay */}
      {showMenu && (
        <div
          className="export-overlay"
          onClick={() => setShowMenu(false)}
        />
      )}
    </div>
  );
}

export default ExportButton;
